import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { BulkImportReport } from '@pms/shared';
import { validateBulkImport, commitBulkImport } from '@/services/api/bulk-import.api';
import { platformKeys } from './usePlatform';

// ─── Bulk import mutations (Hotel Groups — Slice 2) ─────────────────────────────

interface ValidateVars {
  file: File;
  targetTenantId?: string;
}

interface CommitVars {
  file: File;
  targetTenantId?: string;
  /** Minted once per wizard session so a retried commit replays, never re-creates. */
  idempotencyKey: string;
}

/** Validate an uploaded file into a per-row preview report (no writes). */
export function useValidateBulkImport() {
  return useMutation<BulkImportReport, unknown, ValidateVars>({
    mutationFn: ({ file, targetTenantId }) => validateBulkImport(file, targetTenantId),
  });
}

/**
 * Commit the valid rows. On success invalidates the platform properties list so
 * the newly-created DRAFT rows show up in `/admin/properties` without a manual
 * refresh.
 */
export function useCommitBulkImport() {
  const queryClient = useQueryClient();
  return useMutation<BulkImportReport, unknown, CommitVars>({
    mutationFn: ({ file, targetTenantId, idempotencyKey }) =>
      commitBulkImport(file, targetTenantId, idempotencyKey),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: platformKeys.properties });
    },
  });
}
